import { create } from 'zustand';
import { reminderApi } from '../services/api';
import { websocketService } from '../services/websocket';

// Reminder type matching the shape used in RemindersPage
interface RecurringRule {
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  interval: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  endDate?: string;
  maxOccurrences?: number;
}

export interface Reminder {
  id: string;
  title: string;
  description: string | null;
  remindAt: string;
  recurringRule: RecurringRule | null;
  creatorId: string;
  recipientId: string;
  isCompleted: boolean;
  completedAt: string | null;
  isPaused: boolean;
  taskId: string | null;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  recipient: {
    id: string;
    firstName: string;
    lastName: string;
    telegramChatId: string | null;
  };
  task?: {
    id: string;
    title: string;
  } | null;
  notifications: {
    id: string;
    channel: string;
    status: string;
    scheduledFor: string;
  }[];
}

interface ReminderState {
  reminders: Reminder[];
  isLoading: boolean;
  error: string | null;
  filter: 'pending' | 'recurrent' | 'completed';
  setFilter: (filter: 'pending' | 'recurrent' | 'completed') => void;
  fetchReminders: () => Promise<void>;
  // Real-time WebSocket subscriptions
  subscribeToRealTimeUpdates: () => void;
  unsubscribeFromRealTimeUpdates: () => void;
}

// Real-time WebSocket subscription cleanup functions
const realtimeUnsubscribers: Array<() => void> = [];

export const useReminderStore = create<ReminderState>((set, get) => ({
  reminders: [],
  isLoading: false,
  error: null,
  filter: 'pending',

  setFilter: (filter) => {
    set({ filter });
  },

  fetchReminders: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await reminderApi.list({ status: get().filter });
      if (response.success && response.data) {
        set({ reminders: response.data as Reminder[], isLoading: false });
      } else {
        set({ error: 'Failed to fetch reminders', isLoading: false });
      }
    } catch {
      set({ error: 'Failed to fetch reminders', isLoading: false });
    }
  },

  subscribeToRealTimeUpdates: () => {
    // Unsubscribe from any existing subscriptions first
    get().unsubscribeFromRealTimeUpdates();

    // reminder:created — prepend new reminder (dedup: skip if already exists)
    realtimeUnsubscribers.push(
      websocketService.onReminderCreated((data) => {
        const { reminders } = get();
        if (!reminders.some((r) => r.id === data.reminder.id)) {
          // Insert at top, but only if the reminder matches current filter
          // We optimistically add it; the next fetch will reconcile
          set({ reminders: [data.reminder as unknown as Reminder, ...reminders] });
        }
      })
    );

    // reminder:updated — replace matching reminder in list
    realtimeUnsubscribers.push(
      websocketService.onReminderUpdated((data) => {
        set({
          reminders: get().reminders.map((r) =>
            r.id === data.reminder.id ? (data.reminder as unknown as Reminder) : r
          ),
        });
      })
    );

    // reminder:deleted — remove from list
    realtimeUnsubscribers.push(
      websocketService.onReminderDeleted((data) => {
        set({
          reminders: get().reminders.filter((r) => r.id !== data.reminderId),
        });
      })
    );

    // reminder:completed — replace matching reminder (marks completed)
    realtimeUnsubscribers.push(
      websocketService.onReminderCompleted((data) => {
        set({
          reminders: get().reminders.map((r) =>
            r.id === data.reminder.id ? (data.reminder as unknown as Reminder) : r
          ),
        });
      })
    );
  },

  unsubscribeFromRealTimeUpdates: () => {
    realtimeUnsubscribers.forEach((unsub) => unsub());
    realtimeUnsubscribers.length = 0;
  },
}));
